import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { AllCommunityModule, ModuleRegistry, themeQuartz } from 'ag-grid-community';
import '../CommonDataTable.css';
import {
  BuiltYearFilter,
  MoneyRangeFilter,
  HouseholdRangeFilter,
  AreaRangeFilter,
  MultiSelectListFilter,
  CountRangeFilter,
} from '../ranking/filters';

ModuleRegistry.registerModules([AllCommunityModule]);

const AG_GRID_LOCALE_KO = {
  page: '페이지',
  more: '더보기',
  to: '~',
  of: '/',
  next: '다음',
  last: '마지막',
  first: '처음',
  previous: '이전',
  loadingOoo: '불러오는 중...',
  noRowsToShow: '표시할 데이터가 없습니다.',
  filterOoo: '필터...',
  equals: '같음',
  notEqual: '같지 않음',
  lessThan: '보다 작음',
  greaterThan: '보다 큼',
  lessThanOrEqual: '이하',
  greaterThanOrEqual: '이상',
  inRange: '범위',
  contains: '포함',
  notContains: '포함 안 함',
  startsWith: '시작 문자',
  endsWith: '끝 문자',
  blank: '빈 값',
  notBlank: '빈 값 아님',
  andCondition: '그리고',
  orCondition: '또는',
  applyFilter: '적용',
  resetFilter: '초기화',
  clearFilter: '지우기',
  cancelFilter: '취소',
  textFilter: '텍스트 필터',
  numberFilter: '숫자 필터',
  dateFilter: '날짜 필터',
  setFilter: '값 필터',
  selectAll: '전체 선택',
  searchOoo: '검색...',
  blanks: '(빈 값)',
  noMatches: '일치 항목 없음',
  before: '이전',
  after: '이후',
  today: '오늘',
};

const HEADER_TOOLTIP_TEXT_BY_LABEL = {
  '지역': '시/구',
  '생활권': '동',
  '지역급지': '시/구 단위 급지',
  '생활권급지': '동 단위 급지',
  '규제': '부동산 규제 정보',
  '인구': '시/구 단위 인구',
  '구분': '단지 그룹',
  '평세대수': '평형별 세대수',
  '임대수': '임대 세대수',
  '구조': '계단/복도 방수 화장실수',
  '고현율': '전고점 대비 현재 변동율',
  '적현율': '적고가 대비 현재 변동율',
  '고저율': '전고점 대비 전저점 변동율',
  '저현율': '전저점 대비 현재 변동율',
  '변동차': '전고점 대비 현재가 매매가 차이',
  '투자금': '매매가-전세가 or 분양권 투자금',
  '실투자금': '매매가-전세가-취득세-필요경비',
  '최고가': '역대 최고가',
  '전고가': '전고점 매매가',
  '적고가': '적절한 전고점 매매가',
  '전저가': '전고점 이후 가장 낮은 매매가',
  '상승률': '16년 초 평균매매가 대비 상승율',
  '상승율': '16년 초 평균매매가 대비 상승율',
  '상승가': '16년 초 평균매매가',
  '과거가': '16년 초 평균 매매가',
  '초기 투자금': '16년 초 평균 투자금',
  '수익률': '초기투자금 대비 현재 매매가 수익률',
};

const getHeaderTooltipText = (label) => {
  const text = HEADER_TOOLTIP_TEXT_BY_LABEL[String(label || '').trim()];
  return text ? String(text) : undefined;
};


// 필터 ON 컬럼 헤더: 라벨 + 슬라이더 아이콘, 필터가 적용되면 그 아래에 값 칩(× 포함) 표시
function AptFilterHeader(props) {
  const { displayName, column, context, showFilterIcon } = props;
  const colId = column?.getColId ? column.getColId() : (column?.getId ? column.getId() : '');
  const summary = context?.filterSummary?.[colId];
  const onRemove = context?.onRemoveHeaderFilter;
  const stop = (e) => { e.stopPropagation(); };
  return (
    <div className="apt-fh">
      <div className="apt-fh-labelrow">
        <span className="apt-fh-label">{displayName}</span>
        {showFilterIcon ? <span className="apt-fh-icon" aria-hidden="true" /> : null}
      </div>
      {summary ? (
        <span className="apt-fh-chip" title={summary} onMouseDown={stop} onClick={stop}>
          <span className="apt-fh-chip-text">{summary}</span>
          <button
            type="button"
            className="apt-fh-chip-x"
            aria-label="필터 제거"
            onMouseDown={stop}
            onClick={(e) => { e.stopPropagation(); e.preventDefault(); if (onRemove) onRemove(colId); }}
          >
            ×
          </button>
        </span>
      ) : null}
    </div>
  );
}

// 조건부 서식: 셀 숫자값 v 가 규칙 r 을 만족하는지
const colorRuleMatches = (v, r) => {
  const t = Number(r?.value);
  if (!Number.isFinite(t) && r?.op !== 'between') return false;
  switch (r?.op) {
    case '>=': return v >= t;
    case '>': return v > t;
    case '<=': return v <= t;
    case '<': return v < t;
    case '=': return v === t;
    case 'between': {
      const t2 = Number(r?.value2);
      if (!Number.isFinite(t) || !Number.isFinite(t2)) return false;
      const lo = Math.min(t, t2);
      const hi = Math.max(t, t2);
      return v >= lo && v <= hi;
    }
    default: return false;
  }
};

function ComplexTanStackTable({
  className = '',
  columns = [],
  rows = [],
  loading = false,
  sorting = [],
  onSortingChange,
  onHeaderClick,
  rowKey,
  onRowClick,
  onColumnReorder,
  isColumnDraggable,
  rowClassName,
  emptyText = '표시할 데이터가 없습니다.',
  showEmpty = true,
  headerFilterEnabled = false,
  onHeaderFilterTrigger,
  extraActiveFilterColumnIds = [],
  headerFilterTargetKeys = [],
  moneyFilterTargetKeys = [],
  countFilterTargetKeys = [],
  numericAlignColumnKeys = [],
  freezeBuildingNameColumn = false,
  onHeaderFilterSummaryChange,
  headerFilterSummaryMap = {}, // 부모가 전달하는 실제 적용 필터 요약 { colKey: text }
  onRemoveHeaderFilter, // 헤더 칩의 × 클릭 시 해당 컬럼 필터 제거 (colKey) => void
  headerFilterResetToken = 0,
  headerFilterRemoveSignal = null,
  onLoadMore, // 무한 스크롤 콜백
  hasMore = false, // 더 로드할 데이터 있는지
  useInfiniteScroll = false, // 무한 스크롤 모드 활성화
  resizeColumnsToken = 0, // 값이 바뀌면 컬럼 너비를 내용에 맞게 재설정
  keepRowsOnTop, // (rowData) => boolean : true 인 행은 정렬과 무관하게 항상 최상단에 고정
  maintainColumnOrder = true, // false 면 columnDefs 순서를 항상 우선(드래그 재정렬 불가 테이블용)
  colorRules = [], // 조건부 서식 규칙: [{ id, scope:'column'|'group', targetKey, op, value, value2?, color }]
  getCellTextColor = null, // (columnKey, rowData) => color|null : 셀 글자색 직접 지정(예: 전주대비 트렌드)
}) {
  const gridRef = useRef(null);
  const topScrollRef = useRef(null);
  const contentScrollRef = useRef(null);
  const [topScrollWidth, setTopScrollWidth] = useState(0);
  // 상단 스크롤바를 하단 AG Grid 가로 스크롤바와 동일한 위치/폭으로 맞추기 위한 값
  const [topScrollOffset, setTopScrollOffset] = useState(0); // 고정(핀)칼럼 폭
  const [topScrollViewport, setTopScrollViewport] = useState(0); // 스크롤 영역(중앙) 가시 폭
  const [hasHScroll, setHasHScroll] = useState(false); // 가로 넘침이 있을 때만 상단 스크롤바 표시
  const syncingScrollRef = useRef(false);
  const buildingNamePinnedRef = useRef(false);
  const buildingPinThresholdRef = useRef(0);
  const [activeFilterColumnIds, setActiveFilterColumnIds] = useState(new Set());
  const [headerFilterSummaryMapLocal, setHeaderFilterSummaryMapLocal] = useState({});
  const loadMoreCalledRef = useRef(false); // 중복 호출 방지
  // 컬럼 구조 변경(화면설정/확장토글/단위표기) 시 autoSize 재계산 동안 레이아웃이 뚝뚝 바뀌는 걸 가리는 오버레이
  const [relayouting, setRelayouting] = useState(false);
  const colSigRef = useRef('');
  const relayoutDoneTimerRef = useRef(null);

  // loading이 false가 되면 플래그 리셋
  useEffect(() => {
    if (!loading) {
      loadMoreCalledRef.current = false;
    }
  }, [loading]);

  const updateActiveFilterColumnIds = useCallback((api) => {
    if (!api || typeof api.getFilterModel !== 'function') return;
    const model = api.getFilterModel() || {};
    const next = new Set(Object.keys(model).filter((key) => model?.[key]));
    setActiveFilterColumnIds(next);
    if (typeof onHeaderFilterSummaryChange === 'function') {
      const summary = Object.entries(model).reduce((acc, [key, raw]) => {
        const colKey = String(key || '').trim();
        if (!colKey || !raw || typeof raw !== 'object') return acc;
        const filterType = String(raw.filterType || '').toLowerCase();
        let text = '';
        if (filterType === 'set' && Array.isArray(raw.values)) {
          text = raw.values.map((v) => String(v || '').trim()).filter(Boolean).join(', ');
        } else if (Array.isArray(raw.selected)) {
          text = raw.selected.map((v) => String(v || '').trim()).filter(Boolean).join(', ');
        } else if (raw.filter != null && String(raw.filter).trim()) {
          text = String(raw.filter).trim();
        } else if (raw.from != null || raw.to != null || raw.min != null || raw.max != null) {
          const start = String(raw.from ?? raw.min ?? '').trim();
          const end = String(raw.to ?? raw.max ?? '').trim();
          text = [start, end].filter(Boolean).join('~');
        }
        if (!text) return acc;
        return { ...acc, [colKey]: text };
      }, {});
      setHeaderFilterSummaryMapLocal(summary);
      onHeaderFilterSummaryChange(summary);
    } else {
      const summary = Object.entries(model).reduce((acc, [key, raw]) => {
        const colKey = String(key || '').trim();
        if (!colKey || !raw || typeof raw !== 'object') return acc;
        const filterType = String(raw.filterType || '').toLowerCase();
        let text = '';
        if (filterType === 'set' && Array.isArray(raw.values)) {
          text = raw.values.map((v) => String(v || '').trim()).filter(Boolean).join(', ');
        } else if (Array.isArray(raw.selected)) {
          text = raw.selected.map((v) => String(v || '').trim()).filter(Boolean).join(', ');
        } else if (raw.filter != null && String(raw.filter).trim()) {
          text = String(raw.filter).trim();
        } else if (raw.from != null || raw.to != null || raw.min != null || raw.max != null) {
          const start = String(raw.from ?? raw.min ?? '').trim();
          const end = String(raw.to ?? raw.max ?? '').trim();
          text = [start, end].filter(Boolean).join('~');
        }
        if (!text) return acc;
        return { ...acc, [colKey]: text };
      }, {});
      setHeaderFilterSummaryMapLocal(summary);
    }
  }, [onHeaderFilterSummaryChange]);

  const leafColumnKeys = useMemo(() => {
    const keys = [];
    const visit = (col) => {
      if (!col) return;
      const children = Array.isArray(col?.columns) ? col.columns : [];
      if (children.length > 0) {
        children.forEach(visit);
        return;
      }
      const key = String(col?.key || '').trim();
      if (key) keys.push(key);
    };
    (Array.isArray(columns) ? columns : []).forEach(visit);
    return keys;
  }, [columns]);

  // 필터 요약은 더 이상 별도의 고정 행(pinned row)으로 표시하지 않고, 각 컬럼 헤더 안에 칩으로 표시한다.
  const pinnedFilterSummaryRows = useMemo(() => [], []);

  // 부모 전달 요약(서버사이드 필터) + 내부 AG 필터 요약 병합 — 헤더 칩에 사용
  const mergedFilterSummary = useMemo(
    () => ({ ...(headerFilterSummaryMap || {}), ...(headerFilterSummaryMapLocal || {}) }),
    [headerFilterSummaryMap, headerFilterSummaryMapLocal]
  );

  // AG Grid context — 커스텀 헤더에서 요약/제거콜백을 읽음
  const gridContext = useMemo(
    () => ({ filterSummary: mergedFilterSummary, onRemoveHeaderFilter }),
    [mergedFilterSummary, onRemoveHeaderFilter]
  );

  // 적용된 필터 요약이 하나라도 있으면 헤더를 높여 값 칩을 담을 공간 확보
  const hasAnyFilterSummary = useMemo(
    () => Object.values(mergedFilterSummary).some((v) => String(v ?? '').trim()),
    [mergedFilterSummary]
  );

  // 요약(적용 필터)이 바뀌면 헤더를 다시 그려 칩을 갱신
  useEffect(() => {
    const api = gridRef.current?.api;
    if (api && typeof api.refreshHeader === 'function') api.refreshHeader();
  }, [mergedFilterSummary, headerFilterEnabled]);

  const mergedActiveFilterColumnIdSet = useMemo(() => {
    const next = new Set(activeFilterColumnIds);
    (Array.isArray(extraActiveFilterColumnIds) ? extraActiveFilterColumnIds : []).forEach((id) => {
      const key = String(id || '').trim();
      if (key) next.add(key);
    });
    return next;
  }, [activeFilterColumnIds, extraActiveFilterColumnIds]);
  const headerFilterTargetKeySet = useMemo(
    () => new Set((Array.isArray(headerFilterTargetKeys) ? headerFilterTargetKeys : []).map((v) => String(v || '').trim()).filter(Boolean)),
    [headerFilterTargetKeys]
  );
  const moneyFilterTargetKeySet = useMemo(
    () => new Set((Array.isArray(moneyFilterTargetKeys) ? moneyFilterTargetKeys : []).map((v) => String(v || '').trim()).filter(Boolean)),
    [moneyFilterTargetKeys]
  );
  const countFilterTargetKeySet = useMemo(
    () => new Set((Array.isArray(countFilterTargetKeys) ? countFilterTargetKeys : []).map((v) => String(v || '').trim()).filter(Boolean)),
    [countFilterTargetKeys]
  );
  const numericAlignColumnKeySet = useMemo(
    () => new Set((Array.isArray(numericAlignColumnKeys) ? numericAlignColumnKeys : []).map((v) => String(v || '').trim()).filter(Boolean)),
    [numericAlignColumnKeys]
  );

  const toLeafColDef = useCallback((column, isGroupEnd = false, groupId = '') => {
    const key = String(column?.key || '');
    const accessorKey = column?.dataKey || key;
    // 조건부 서식: 이 컬럼(scope='column') 또는 소속 그룹(scope='group')을 타깃하는 규칙
    const applicableRules = (Array.isArray(colorRules) ? colorRules : []).filter((r) => {
      if (!r || !r.color) return false;
      if (r.scope === 'group') return String(r.targetKey) === String(groupId);
      return String(r.targetKey) === key;
    });
    const shouldFreezeBuildingName = freezeBuildingNameColumn && key === 'buildingName';
    const filterable = !!column?.filterable;
    const isHeaderFilterTarget = headerFilterEnabled && (filterable || headerFilterTargetKeySet.has(key));
    // 상세필터로 적용된 값 요약이 있으면 필터 모드와 무관하게 칩을 표기
    const hasActiveSummary = !!String((mergedFilterSummary || {})[key] ?? '').trim();
    // 필터 ON일 때 헤더 우측 필터 아이콘(약 18px) 자리만큼 컬럼 폭을 넓혀 글자 밀림 방지
    const filterIconExtraWidth = isHeaderFilterTarget ? 22 : 0;
    const canMove = typeof isColumnDraggable === 'function' ? isColumnDraggable(key) : true;
    const isBuiltDateFilter = key === 'useApproveDate';
    const isHouseholdFilter = key === 'familyCnt';
    const isAreaFilter = key === 'planTypeName';
    const isMoneyFilter = moneyFilterTargetKeySet.has(key);
    const isCountFilter = countFilterTargetKeySet.has(key);
    const customFilterComponent = isBuiltDateFilter
      ? BuiltYearFilter
      : (isHouseholdFilter
        ? HouseholdRangeFilter
        : (isAreaFilter ? AreaRangeFilter : (isMoneyFilter ? MoneyRangeFilter : (isCountFilter ? CountRangeFilter : null))));
    const useSetFilter = filterable && !customFilterComponent;

    const isNumericColumn = numericAlignColumnKeySet.has(key);
    const hasSortValue = typeof column?.sortValue === 'function';

    return {
      colId: key,
      field: accessorKey,
      headerName: String(column?.label || key),
      headerTooltip: getHeaderTooltipText(column?.label || key),
      pinned: (column?.fixed || shouldFreezeBuildingName) ? 'left' : undefined,
      lockPinned: !!(column?.fixed || shouldFreezeBuildingName),
      // 필터 OFF일 때만 정렬 가능
      sortable: !headerFilterEnabled && !!column?.sortable,
      comparator: hasSortValue
        ? (valueA, valueB, nodeA, nodeB, isDescending) => {
          const rowA = nodeA?.data;
          const rowB = nodeB?.data;
          if (!rowA || !rowB) return 0;
          const sortA = column.sortValue(rowA);
          const sortB = column.sortValue(rowB);
          if (sortA == null && sortB == null) return 0;
          if (sortA == null) return 1;
          if (sortB == null) return -1;
          if (typeof sortA === 'number' && typeof sortB === 'number') {
            return sortA - sortB;
          }
          return String(sortA).localeCompare(String(sortB));
        }
        : undefined,
      // AG Grid 기본 필터는 항상 비활성화 (커스텀 필터 사용)
      filter: false,
      floatingFilter: false,
      suppressHeaderMenuButton: true,
      suppressHeaderFilterButton: true,
      suppressMenu: true,
      menuTabs: [],
      filterParams: filterable && !customFilterComponent
        ? {
          buttons: ['reset'],
          closeOnApply: false,
        }
        : undefined,
      suppressMovable: !canMove,
      minWidth: (column?.minWidth || 56) + filterIconExtraWidth,
      maxWidth: column?.maxWidth ? column.maxWidth + filterIconExtraWidth : undefined,
      width: column?.width ? column.width + filterIconExtraWidth : undefined,
      // flex 미사용: 컬럼을 100%로 늘리지 않고 지정/내용 너비대로. 남는 우측 공간은 비워둔다.
      resizable: column?.resizable !== false,
      headerClass: [
        'aptrank-header-center',
        column?.headerClassName || '',
        isHeaderFilterTarget ? 'aptrank-filter-header-target' : '',
        isHeaderFilterTarget && mergedActiveFilterColumnIdSet.has(key) ? 'aptrank-filter-header-on' : '',
        isGroupEnd ? 'apt-group-end' : '',
      ].filter(Boolean).join(' '),
      cellClass: [
        column?.className || '',
        // %처럼 수치 컬럼은 align:'center' 지정 시 가운데 정렬(우측정렬보다 우선)
        column?.align === 'center' ? 'aptrank-cell-center' : (isNumericColumn ? 'aptrank-cell-right' : ''),
        isGroupEnd ? 'apt-group-end' : '',
      ].filter(Boolean).join(' '),
      cellStyle: (applicableRules.length || typeof getCellTextColor === 'function')
        ? (params) => {
          const d = params?.data;
          if (!d || d.__isFilterSummaryRow) return null;
          let style = null;
          // 1) 셀 글자색(전주대비 트렌드 등) — 컬럼 key + 행 데이터로 판별
          if (typeof getCellTextColor === 'function') {
            const c = getCellTextColor(key, d);
            if (c) style = { color: c };
          }
          // 2) 조건부 서식(배경색 규칙)
          const v = Number(params?.value);
          if (Number.isFinite(v)) {
            for (const r of applicableRules) {
              if (colorRuleMatches(v, r)) { style = { ...(style || {}), backgroundColor: r.color }; break; }
            }
          }
          return style;
        }
        : undefined,
      valueGetter: (params) => (params?.data?.__isFilterSummaryRow
        ? params.data[key]              // 필터 요약행은 컬럼 key 로 저장됨
        : params?.data?.[accessorKey]),
      filterValueGetter: (isBuiltDateFilter || isHouseholdFilter || isMoneyFilter || isCountFilter)
        ? (params) => {
          const raw = params?.data?.[accessorKey];
          if (isBuiltDateFilter) {
            const year = Number(String(raw || '').slice(0, 4).replace(/\D/g, ''));
            return Number.isFinite(year) ? year : null;
          }
          if (isMoneyFilter) {
            const amount = Number(raw);
            return Number.isFinite(amount) ? amount : null;
          }
          if (isCountFilter) {
            const count = Number(raw);
            return Number.isFinite(count) ? count : null;
          }
          const count = Number(raw);
          return Number.isFinite(count) ? count : null;
        }
        : undefined,
      cellRenderer: typeof column?.render === 'function'
        // 필터 요약행에서는 커스텀 render 를 우회하고 요약 텍스트(valueGetter 값)를 그대로 표시
        ? (params) => (params?.data?.__isFilterSummaryRow
          ? (params?.value ?? '')
          : column.render(params?.data, params?.rowIndex))
        : undefined,
      cellRendererParams: {
        filterable: filterable,
      },
      // 커스텀 헤더 컴포넌트(예: 전체선택 체크박스) 지원.
      // 지정된 게 없고 (필터 대상이거나 적용된 요약이 있으면) 필터 값 칩(× 포함)을 그리는 헤더 사용.
      // 필터 모드가 아니어도 상세필터로 적용된 값은 칼럼에 칩으로 표기한다.
      headerComponent: typeof column?.headerComponent === 'function'
        ? column.headerComponent
        : ((isHeaderFilterTarget || hasActiveSummary) ? AptFilterHeader : undefined),
      // 헤더 컴포넌트에서 filterable 정보를 전달하기 위한 추가 속성
      headerComponentParams: {
        filterable: filterable,
        // 슬라이더(필터) 아이콘은 필터 ON 모드에서만, 칩은 요약이 있으면 항상
        showFilterIcon: isHeaderFilterTarget,
        ...(column?.headerComponentParams || {}),
      },
    };
  }, [freezeBuildingNameColumn, headerFilterEnabled, headerFilterTargetKeySet, isColumnDraggable, moneyFilterTargetKeySet, countFilterTargetKeySet, numericAlignColumnKeySet, mergedActiveFilterColumnIdSet, mergedFilterSummary, colorRules, getCellTextColor]);

  const toColDef = useCallback((column, index = 0, isGroupEnd = false, groupId = '') => {
    const children = Array.isArray(column?.columns) ? column.columns.filter(Boolean) : [];
    if (children.length === 0) return toLeafColDef(column, isGroupEnd, groupId);

    return {
      colId: String(column?.key || column?.id || `group-${index}`),
      headerName: String(column?.label || ''),
      headerTooltip: getHeaderTooltipText(column?.label),
      // 그룹 헤더 우측에도 경계 실선(리프/본문의 apt-group-end 와 이어져 full-height 세로선)
      headerClass: ['common-table-th-group', column?.headerClassName || '', isGroupEnd ? 'apt-group-end' : ''].filter(Boolean).join(' '),
      // 그룹 헤더 커스텀 컴포넌트(예: 세금 그룹 톱니) 통과
      ...(column?.headerGroupComponent ? {
        headerGroupComponent: column.headerGroupComponent,
        headerGroupComponentParams: column.headerGroupComponentParams || {},
      } : {}),
      // 그룹 내 마지막 자식에 그룹 경계(우측 실선) 표시
      children: children.map((child, childIndex) => toColDef(child, childIndex, childIndex === children.length - 1, groupId)),
      marryChildren: true,
      suppressMovable: true,
    };
  }, [toLeafColDef]);

  // 그룹 교차 배경 사용 안 함 → 그룹 경계는 실선(apt-group-end)으로만 구분.
  // 각 최상위 그룹의 groupId 를 리프까지 전달(조건부 서식의 그룹 스코프 매칭용).
  const columnDefs = useMemo(() => {
    return (Array.isArray(columns) ? columns : []).map((column, index) => {
      const groupId = String(column?.key || '').replace(/^group__/, '');
      return toColDef(column, index, false, groupId);
    });
  }, [columns, toColDef]);

  // 조건부 서식 규칙 변경 시 셀 배경 즉시 갱신
  useEffect(() => {
    const api = gridRef.current?.api;
    if (api && typeof api.refreshCells === 'function') {
      try { api.refreshCells({ force: true }); } catch { /* grid not ready */ }
    }
  }, [colorRules]);

  const complexInfoTriggerColId = useMemo(() => {
    const list = Array.isArray(columns) ? columns : [];
    const complexInfoGroup = list.find((col) => String(col?.key || '') === 'group__complexInfo');
    const children = Array.isArray(complexInfoGroup?.columns) ? complexInfoGroup.columns : [];
    const firstLeaf = children.find((child) => String(child?.key || '').trim());
    return String(firstLeaf?.key || 'buildingName');
  }, [columns]);

  const getRowId = useCallback((params) => {
    const row = params?.data;
    if (typeof rowKey === 'function') {
      return String(rowKey(row, params?.rowIndex));
    }
    return String(row?.[rowKey] ?? params?.rowIndex ?? '');
  }, [rowKey]);

  const handleRowClicked = useCallback((event) => {
    if (!onRowClick) return;
    onRowClick(event?.data, event?.rowIndex, event);
  }, [onRowClick]);

  const handleColumnMoved = useCallback((event) => {
    if (!onColumnReorder || !event?.column || !event?.finished || !Number.isFinite(event?.toIndex)) return;
    const fromColumnId = String(event.column.getColId() || '');
    const displayed = event.api.getAllDisplayedColumns();
    const target = displayed[event.toIndex];
    const toColumnId = String(target?.getColId?.() || '');
    if (!fromColumnId || !toColumnId || fromColumnId === toColumnId) return;
    onColumnReorder(fromColumnId, toColumnId);
  }, [onColumnReorder]);

  const openColumnFilterPopup = useCallback((event, colId) => {
    const api = event?.api || gridRef.current?.api;
    if (!api || !colId) return;
    const column = event?.column;
    const headerCell = gridRef.current?.eGridDiv?.querySelector(`.ag-header-cell[col-id="${colId}"]`);
    if (typeof api.showColumnFilter === 'function') {
      api.showColumnFilter(colId);
      return;
    }
    if (typeof api.showColumnMenuAfterMouseClick === 'function' && headerCell) {
      const rect = headerCell.getBoundingClientRect();
      api.showColumnMenuAfterMouseClick(column, {
        clientX: rect.left + Math.min(rect.width, 28),
        clientY: rect.bottom - 2,
      });
      return;
    }
    if (typeof api.showColumnMenu === 'function') {
      api.showColumnMenu(column);
    }
  }, []);

  const handleColumnHeaderClicked = useCallback((event) => {
    const colId = String(event?.column?.getColId?.() || '');
    if (!colId) return;
    // 헤더 칩(값 + ×) 영역 클릭은 필터 팝업을 열지 않음 (× 는 제거만)
    const domTarget = event?.event?.target;
    if (domTarget && typeof domTarget.closest === 'function' && domTarget.closest('.apt-fh-chip')) {
      return;
    }
    const colDef = event?.column?.getColDef?.();

    const filterable = colDef?.cellRendererParams?.filterable || colDef?.headerComponentParams?.filterable;

    console.log('Header clicked:', {
      colId,
      headerFilterEnabled,
      cellRendererParams: colDef?.cellRendererParams,
      headerComponentParams: colDef?.headerComponentParams,
      filterable: filterable
    });

    // 필터 ON일 때만 커스텀 필터 UI 표시
    if (headerFilterEnabled && filterable) {
      if (typeof onHeaderFilterTrigger === 'function') {
        // AG Grid의 gridRef를 통해 헤더 element 찾기
        let rect = null;

        console.log('Attempting to find header:', {
          hasGridRef: !!gridRef.current,
          gridRefKeys: gridRef.current ? Object.keys(gridRef.current).slice(0, 10) : [],
          hasApi: !!gridRef.current?.api,
          colId
        });

        // AG Grid React의 ref는 api 객체를 포함하고, DOM 요소는 document에서 직접 찾아야 함
        const gridDiv = document.querySelector('.ag-root');

        console.log('Grid div element:', {
          hasGridDiv: !!gridDiv,
          querySelector: '.ag-root'
        });

        if (gridDiv) {
          // querySelector로 헤더 셀 찾기
          const headerCell = gridDiv.querySelector(`.ag-header-cell[col-id="${colId}"]`);
          console.log('Found header cell:', { colId, headerCell });

          if (headerCell) {
            rect = headerCell.getBoundingClientRect();
            console.log('Header rect:', rect);
          } else {
            console.log('Header cell not found, trying alternative selectors');
            // 모든 헤더 조회
            const allHeaders = gridDiv.querySelectorAll('.ag-header-cell');
            console.log('Total headers found:', allHeaders.length);
          }
        } else {
          console.log('Grid div is not available');
        }

        const handled = onHeaderFilterTrigger(colId, {
          event,
          anchorRect: rect,
        });

        console.log('onHeaderFilterTrigger result:', { handled, rect });
        if (handled) return;
      }
      return;
    }

    // 필터 OFF일 때는 일반 헤더 클릭 이벤트 (정렬 등)
    if (typeof onHeaderClick !== 'function') return;
    onHeaderClick(colId, event);
  }, [headerFilterEnabled, onHeaderFilterTrigger, onHeaderClick, openColumnFilterPopup]);

  const handleSortChanged = useCallback((event) => {
    if (typeof onSortingChange !== 'function' || !event?.api) return;
    const columnState = typeof event.api.getColumnState === 'function'
      ? event.api.getColumnState()
      : [];
    const nextSorting = (Array.isArray(columnState) ? columnState : [])
      .filter((col) => col?.sort === 'asc' || col?.sort === 'desc')
      .sort((a, b) => Number(a?.sortIndex ?? 0) - Number(b?.sortIndex ?? 0))
      .map((col) => ({
        id: String(col?.colId || ''),
        desc: String(col?.sort || '') === 'desc',
      }))
      .filter((row) => row.id);
    onSortingChange(nextSorting);
  }, [onSortingChange]);

  const getRowClass = useCallback((params) => {
    const custom = typeof rowClassName === 'function' ? rowClassName(params?.data, params?.rowIndex) : '';
    const summary = params?.node?.rowPinned === 'top' ? 'aptrank-filter-summary-row' : '';
    return [custom, summary].filter(Boolean).join(' ');
  }, [rowClassName]);

  const handleFilterChanged = useCallback((event) => {
    updateActiveFilterColumnIds(event?.api);
  }, [updateActiveFilterColumnIds]);

  // 정렬 이후에도 특정 행(예: 그룹평균 집계행)을 항상 최상단(0번째)에 고정
  const handlePostSortRows = useCallback((params) => {
    if (typeof keepRowsOnTop !== 'function') return;
    const rowNodes = params?.nodes;
    if (!Array.isArray(rowNodes)) return;
    let insertPos = 0;
    for (let i = 0; i < rowNodes.length; i += 1) {
      const data = rowNodes[i]?.data;
      if (data && keepRowsOnTop(data)) {
        // 원래 순서를 유지하면서 상단으로 이동 (stable)
        rowNodes.splice(insertPos, 0, rowNodes.splice(i, 1)[0]);
        insertPos += 1;
      }
    }
  }, [keepRowsOnTop]);

  const updateBuildingPinThreshold = (api, rootEl = null) => {
    if (freezeBuildingNameColumn) return;
    if (!api) return;

    // 1) AG Grid center 컬럼 순서/실제 너비 기반 임계값 계산 (가장 안정적)
    const centerColumns = typeof api.getAllDisplayedCenterColumns === 'function'
      ? api.getAllDisplayedCenterColumns()
      : [];
    const centerIndex = centerColumns.findIndex((col) => String(col?.getColId?.() || '') === 'buildingName');
    if (centerIndex === 0) {
      // 단지명이 center 첫 컬럼이면, 가로 스크롤이 시작되는 순간 고정되도록 최소 임계값 사용
      buildingPinThresholdRef.current = 1;
      return;
    }
    if (centerIndex > 0) {
      const threshold = centerColumns
        .slice(0, centerIndex)
        .reduce((acc, col) => acc + Number(col?.getActualWidth?.() || 0), 0);
      if (Number.isFinite(threshold) && threshold > 0) {
        buildingPinThresholdRef.current = threshold;
        return;
      }
    }

    // 2) center left 값 fallback
    const centerBuildingColumn = centerColumns.find((col) => String(col?.getColId?.() || '') === 'buildingName');
    const centerLeft = Number(centerBuildingColumn?.getLeft?.());
    if (Number.isFinite(centerLeft) && centerLeft > 0) {
      buildingPinThresholdRef.current = centerLeft;
      return;
    }

    // 3) DOM fallback
    const root = rootEl || gridRef.current?.eGridDiv;
    if (!root) return;
    const centerViewport = root.querySelector('.ag-center-cols-viewport');
    const headerCell = root.querySelector('.ag-header-cell[col-id="buildingName"]');
    if (!centerViewport || !headerCell) return;
    const threshold = Number(headerCell.offsetLeft || 0);
    if (Number.isFinite(threshold) && threshold > 0) {
      buildingPinThresholdRef.current = threshold;
      return;
    }

    // 4) 최종 fallback: 계산이 불가능한 경우 스크롤 시작 시점부터 고정
    buildingPinThresholdRef.current = 1;
  };

  const syncBuildingNamePinnedByScrollLeft = (left) => {
    if (freezeBuildingNameColumn) return;
    const gridApi = gridRef.current?.api;
    if (!gridApi) return;
    const scrollLeft = Number(left || 0);
    const currentlyPinned = buildingNamePinnedRef.current;
    const threshold = Number(buildingPinThresholdRef.current || 1);
    const shouldPinBuildingName = currentlyPinned
      ? scrollLeft > 0.5
      : scrollLeft >= Math.max(1, threshold - 1);
    if (currentlyPinned === shouldPinBuildingName) return;
    buildingNamePinnedRef.current = shouldPinBuildingName;
    gridApi.applyColumnState({
      state: [{ colId: 'buildingName', pinned: shouldPinBuildingName ? 'left' : null }],
      applyOrder: false,
    });
  };

  useEffect(() => {
    let cleanupFn = null;
    const retryTimers = [];

    const setup = (attempt) => {
      // AG Grid v32+ 에서는 gridRef.current.eGridDiv 가 노출되지 않으므로 wrapper DOM 을 기준으로 탐색
      const rootEl = contentScrollRef.current || gridRef.current?.eGridDiv;
      const horizontalViewport = rootEl?.querySelector('.ag-body-horizontal-scroll-viewport');
      const horizontalContainer = rootEl?.querySelector('.ag-body-horizontal-scroll-container');
      const centerViewport = rootEl?.querySelector('.ag-center-cols-viewport');
      const centerContainer = rootEl?.querySelector('.ag-center-cols-container');
      const headerContainer = rootEl?.querySelector('.ag-header-container');

      // AG Grid 렌더 전이면 잠시 후 재시도 (상단 스크롤바가 항상 초기화되도록)
      if (!rootEl || !centerViewport || !horizontalViewport) {
        if (attempt < 12) retryTimers.push(setTimeout(() => setup(attempt + 1), 120));
        return;
      }

      const leftSpacer = rootEl.querySelector('.ag-horizontal-left-spacer');
      const updateTopWidth = () => {
        const containerWidth = horizontalContainer?.scrollWidth || horizontalContainer?.offsetWidth || 0;
        const centerContainerWidth = centerContainer?.scrollWidth || centerContainer?.offsetWidth || 0;
        const headerContainerWidth = headerContainer?.scrollWidth || headerContainer?.offsetWidth || 0;
        const viewportScrollWidth = horizontalViewport.scrollWidth || 0;
        const centerScrollWidth = centerViewport.scrollWidth || 0;
        const nextWidth = Math.max(
          containerWidth,
          centerContainerWidth,
          headerContainerWidth,
          viewportScrollWidth,
          centerScrollWidth,
          0
        );
        setTopScrollWidth(nextWidth);
        // 하단 스크롤바와 동일 정렬: 고정칼럼 폭만큼 왼쪽 오프셋 + 스크롤 영역 가시 폭
        setTopScrollOffset(leftSpacer?.offsetWidth || 0);
        setTopScrollViewport(horizontalViewport.clientWidth || 0);
        // 가로 넘침이 있을 때만(하단 스크롤바가 뜨는 상황) 상단 스크롤바 표시
        setHasHScroll((horizontalViewport.scrollWidth || 0) > (horizontalViewport.clientWidth || 0) + 1);
        updateBuildingPinThreshold(gridRef.current?.api, rootEl);
      };

      const syncFromBody = () => {
        if (syncingScrollRef.current) return;
        if (!topScrollRef.current) return;
        syncingScrollRef.current = true;
        const nextLeft = horizontalViewport.scrollLeft;
        topScrollRef.current.scrollLeft = nextLeft;
        syncingScrollRef.current = false;
        syncBuildingNamePinnedByScrollLeft(nextLeft);
      };

      const onTopScroll = (event) => {
        if (syncingScrollRef.current) return;
        syncingScrollRef.current = true;
        const nextLeft = event.currentTarget.scrollLeft;
        horizontalViewport.scrollLeft = nextLeft;
        centerViewport.scrollLeft = nextLeft;
        syncingScrollRef.current = false;
        syncBuildingNamePinnedByScrollLeft(nextLeft);
      };

      updateTopWidth();
      syncFromBody();
      const timers = [0, 80, 180, 320].map((delay) => setTimeout(updateTopWidth, delay));

      horizontalViewport.addEventListener('scroll', syncFromBody, { passive: true });
      topScrollRef.current?.addEventListener('scroll', onTopScroll, { passive: true });

      const resizeObserver = typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(() => {
          updateTopWidth();
          syncFromBody();
        })
        : null;

      resizeObserver?.observe(horizontalViewport);
      if (horizontalContainer) resizeObserver?.observe(horizontalContainer);
      resizeObserver?.observe(centerViewport);
      if (centerContainer) resizeObserver?.observe(centerContainer);
      if (headerContainer) resizeObserver?.observe(headerContainer);

      cleanupFn = () => {
        horizontalViewport.removeEventListener('scroll', syncFromBody);
        topScrollRef.current?.removeEventListener('scroll', onTopScroll);
        timers.forEach((timerId) => clearTimeout(timerId));
        resizeObserver?.disconnect();
      };
    };

    setup(0);

    return () => {
      retryTimers.forEach((timerId) => clearTimeout(timerId));
      cleanupFn?.();
    };
  }, [rows, columns, loading, complexInfoTriggerColId]);

  useEffect(() => {
    const api = gridRef.current?.api;
    if (!api) return;

    // 모든 컬럼에 정렬 상태 적용 (안 보이는 컬럼도 포함, 전체 정렬 순서 유지)
    const nextState = (Array.isArray(sorting) ? sorting : [])
      .map((row, idx) => ({
        colId: String(row?.id || ''),
        sort: row?.desc ? 'desc' : 'asc',
        sortIndex: idx, // 전체 정렬 순서 유지 (안 보이는 컬럼도 번호 포함)
      }))
      .filter((row) => row.colId);

    console.log('Applying sort state:', nextState);

    // applyOrder를 false로 설정하여 sortIndex가 변경되지 않도록 함
    api.applyColumnState({
      state: nextState,
      defaultState: { sort: null },
      applyOrder: false
    });

    // 정렬 상태 적용 후 확인
    setTimeout(() => {
      if (!api) return;
      const currentState = api.getColumnState();
      if (!currentState) return;
      const sortedCols = currentState.filter(col => col.sort);
      console.log('Current sort state after apply:', sortedCols.map(col => ({
        colId: col.colId,
        sort: col.sort,
        sortIndex: col.sortIndex,
        hide: col.hide
      })));
    }, 100);
  }, [sorting, columns]);

  useEffect(() => {
    const api = gridRef.current?.api;
    if (!api) return;
    if (!headerFilterEnabled) return;
    updateActiveFilterColumnIds(api);
  }, [headerFilterEnabled]);

  useEffect(() => {
    if (!headerFilterResetToken) return;
    const api = gridRef.current?.api;
    if (!api) return;
    api.setFilterModel(null);
    api.onFilterChanged();
    setActiveFilterColumnIds(new Set());
    setHeaderFilterSummaryMapLocal({});
    if (typeof onHeaderFilterSummaryChange === 'function') onHeaderFilterSummaryChange({});
  }, [headerFilterResetToken, onHeaderFilterSummaryChange]);

  useEffect(() => {
    const key = String(headerFilterRemoveSignal?.key || '').trim();
    const token = Number(headerFilterRemoveSignal?.token || 0);
    if (!key || !token) return;
    const api = gridRef.current?.api;
    if (!api || typeof api.getFilterModel !== 'function') return;
    const model = { ...(api.getFilterModel() || {}) };
    if (!(key in model)) return;
    delete model[key];
    api.setFilterModel(model);
    api.onFilterChanged();
    updateActiveFilterColumnIds(api);
  }, [headerFilterRemoveSignal]);

  // 자동 너비 계산 후 각 컬럼을 소폭 축소한다.
  // autoSizeAllColumns(false) 는 헤더+내용 실측이라 살짝 여유가 있어서, 이 값만큼 좁힌다.
  // (minWidth 아래로는 줄이지 않음)
  const AUTOSIZE_SHRINK_PX = 16;
  const autoSizeFit = (api) => {
    if (!api || api.isDestroyed?.()) return;
    if (typeof api.autoSizeAllColumns !== 'function') return;
    api.autoSizeAllColumns(false);
    try {
      const cols = api.getColumns?.() || [];
      const state = cols.map((col) => {
        const cur = col.getActualWidth?.() ?? 0;
        const min = col.getMinWidth?.() ?? 0;
        return { colId: col.getColId(), width: Math.max(min || 0, cur - AUTOSIZE_SHRINK_PX) };
      });
      if (state.length) api.applyColumnState?.({ state });
    } catch (e) { /* noop */ }
    updateBuildingPinThreshold(api, gridRef.current?.eGridDiv);
  };

  // 컬럼 너비 재설정: 단위 표기 토글 등으로 셀 내용 길이가 바뀌면 내용에 맞게 자동 너비 재계산
  useEffect(() => {
    if (!resizeColumnsToken) return undefined;
    const id = setTimeout(() => {
      autoSizeFit(gridRef.current?.api);
    }, 60); // 셀이 새 단위 텍스트로 렌더된 뒤 측정
    return () => clearTimeout(id);
  }, [resizeColumnsToken]);

  // 화면설정(표시 컬럼) 변경 등으로 columnDefs 가 바뀌면 즉시 내용 너비 재계산.
  // 컬럼 "구조"(그룹/리프 key 구성)가 바뀔 때만 오버레이로 가린다(데이터 로드엔 안 뜸).
  useEffect(() => {
    const sig = (Array.isArray(columns) ? columns : [])
      .map((g) => `${g?.key || g?.id || ''}#${(Array.isArray(g?.columns) ? g.columns : []).map((c) => c?.key || c?.id || '').join(',')}`)
      .join('|');
    const structureChanged = colSigRef.current !== '' && colSigRef.current !== sig;
    colSigRef.current = sig;
    if (structureChanged) setRelayouting(true);
    const id = setTimeout(() => {
      autoSizeFit(gridRef.current?.api);
      if (relayoutDoneTimerRef.current) clearTimeout(relayoutDoneTimerRef.current);
      // autoSize 후 레이아웃 안정화까지 잠깐 더 가린 뒤 해제
      relayoutDoneTimerRef.current = setTimeout(() => setRelayouting(false), 140);
    }, 60);
    return () => clearTimeout(id);
  }, [columnDefs, columns]);

  useEffect(() => () => { if (relayoutDoneTimerRef.current) clearTimeout(relayoutDoneTimerRef.current); }, []);

  return (
    <div className={`common-table-wrap ${className}`.trim()} style={{ position: 'relative' }}>
      {relayouting && (
        <div className="common-table-relayout-overlay" aria-hidden="true">
          <div className="common-table-relayout-spinner" />
        </div>
      )}
      <div
        className="common-table-top-scroll common-table-top-scroll-ag"
        ref={topScrollRef}
        style={{
          display: hasHScroll ? undefined : 'none',
          marginLeft: topScrollOffset || undefined,
          width: topScrollViewport ? `${topScrollViewport}px` : undefined,
        }}
      >
        <div className="common-table-top-scroll-inner" style={{ width: topScrollWidth }} />
      </div>
      <div className="common-table-content-scroll ag-theme-aptrank" ref={contentScrollRef}>
        <AgGridReact
          ref={gridRef}
          rowData={Array.isArray(rows) ? rows : []}
          pinnedTopRowData={pinnedFilterSummaryRows}
          columnDefs={columnDefs}
          context={gridContext}
          headerHeight={hasAnyFilterSummary ? 60 : undefined}
          groupHeaderHeight={24}
          getRowId={getRowId}
          suppressScrollOnNewData={true}
          onGridReady={(event) => {
            updateBuildingPinThreshold(event?.api, gridRef.current?.eGridDiv);
          }}
          onBodyScroll={(event) => {
            if (!useInfiniteScroll || !hasMore || loading || !onLoadMore) return;
            if (loadMoreCalledRef.current) return; // 이미 호출 중이면 무시

            const gridApi = event.api;
            if (!gridApi) return;

            // 스크롤이 하단에 도달했는지 확인
            const renderedRowCount = gridApi.getDisplayedRowCount();
            if (renderedRowCount === 0) return;

            // 현재 보이는 마지막 행의 인덱스 (AG-Grid v31+ API)
            const firstRenderedIndex = gridApi.getFirstDisplayedRowIndex();
            const lastRenderedIndex = gridApi.getLastDisplayedRowIndex();

            // 마지막 행이 보이면 더 로드 (임계값: 마지막에서 5행 이내)
            if (lastRenderedIndex >= renderedRowCount - 5) {
              loadMoreCalledRef.current = true;
              console.log('[ComplexTanStackTable] Triggering onLoadMore, lastRenderedIndex:', lastRenderedIndex, 'totalCount:', renderedRowCount);
              onLoadMore();
            }
          }}
          onFirstDataRendered={(event) => {
            // 최초 렌더 시 컬럼을 내용 너비에 맞게 자동 조정(우측 남는 공간은 비움)
            autoSizeFit(event?.api);
          }}
          onGridSizeChanged={(event) => {
            // Grid size changed
          }}
          onDisplayedColumnsChanged={(event) => {
            updateBuildingPinThreshold(event?.api, gridRef.current?.eGridDiv);
          }}
          onColumnResized={(event) => {
            if (!event?.finished) return;
            updateBuildingPinThreshold(event?.api, gridRef.current?.eGridDiv);
          }}
          onRowClicked={handleRowClicked}
          onColumnMoved={handleColumnMoved}
          onColumnHeaderClicked={handleColumnHeaderClicked}
          onSortChanged={handleSortChanged}
          onFilterChanged={handleFilterChanged}
          postSortRows={handlePostSortRows}
          alwaysMultiSort
          suppressMultiSort={false}
          rowSelection={{ mode: 'singleRow', checkboxes: false, headerCheckbox: false, enableClickSelection: true }}
          getRowClass={getRowClass}
          animateRows={false}
          resetRowDataOnUpdate={true}
          suppressCellFocus
          suppressRowHoverHighlight={false}
          suppressDragLeaveHidesColumns
          suppressMovableColumns={!onColumnReorder}
          suppressColumnVirtualisation={true}
          maintainColumnOrder={maintainColumnOrder}
          tooltipShowDelay={1000}
          loading={loading}
          noRowsOverlayComponentParams={{ noRowsMessageFunc: () => (showEmpty ? emptyText : '') }}
          localeText={AG_GRID_LOCALE_KO}
          icons={{
            // 위/아래 삼각형이 합쳐진 아이콘 — 적용된 방향의 삼각형만 주황색으로 색칠
            sortAscending: '<span aria-hidden="true" style="display:inline-flex;flex-direction:column;line-height:0.62;font-size:9px;"><span style="color:#ea580c;">▲</span><span style="color:#cbd5e1;">▼</span></span>',
            sortDescending: '<span aria-hidden="true" style="display:inline-flex;flex-direction:column;line-height:0.62;font-size:9px;"><span style="color:#cbd5e1;">▲</span><span style="color:#ea580c;">▼</span></span>',
            sortUnSort: '<span aria-hidden="true" style="display:inline-flex;flex-direction:column;line-height:0.62;font-size:9px;"><span style="color:#cbd5e1;">▲</span><span style="color:#cbd5e1;">▼</span></span>',
          }}
          defaultColDef={{
            sortable: true,
            resizable: true,
            filter: false,
            floatingFilter: false,
            suppressHeaderMenuButton: true,
            suppressHeaderFilterButton: true,
          }}
          autoSizeStrategy={{
            type: 'fitGridWidth',
            columnLimits: [
              { colId: 'ag-Grid-AutoColumn', minWidth: 50, maxWidth: 50 },
              { colId: 'buildingName', minWidth: 180 },
            ],
          }}
          theme={themeQuartz.withParams({
            fontFamily: 'Pretendard, Apple SD Gothic Neo, Malgun Gothic, sans-serif',
            accentColor: '#ea580c',
            headerBackgroundColor: '#fff7ed',
            headerTextColor: '#7c2d12',
            borderColor: '#ecdfd3',
            rowBorder: true,
            rowBorderColor: '#f2ece5',
            headerRowBorder: true,
            headerRowBorderColor: '#fdba74',
            wrapperBorder: false,
            cellHorizontalBorder: true,
            rowHoverColor: '#fff1e0',
            selectedRowBackgroundColor: '#ffedd5',
            oddRowBackgroundColor: '#ffffff',
          })}
        />
      </div>
    </div>
  );
}

export default ComplexTanStackTable;

